import {FC, useState, useEffect} from 'react'
import {Modal, Tab, Tabs} from 'react-bootstrap'

type Config = {
  id?: number
  defaultCleaningTimeMinutes: number
  autoAllocationEnabled: number
  autoReleaseEnabled: number
  allowOverbooking: number
  bufferTimeMinutes: number
  maxTablesPerCombination: number
  maxWastePercentage: number
  preferFewerTables: number
  allowTablesNearby: boolean
  proximityWeight?: number | null
  maxWastedSeatsForBestFit?: number
  preferredDirection?: string
  clusterProximityThreshold?: number
  wasteHandlingMode?: string
  fallbackMaxWastePercentage?: number
}

type Props = {
  show: boolean
  onClose: () => void
  onSave: (data: Omit<Config, 'id'>) => void
  config?: Config
  environmentName: string
}

export const TableConfigModal: FC<Props> = ({show, onClose, onSave, config, environmentName}) => {
  const [formData, setFormData] = useState<Omit<Config, 'id'>>({
    defaultCleaningTimeMinutes: 15,
    autoAllocationEnabled: 1,
    autoReleaseEnabled: 0,
    allowOverbooking: 0,
    bufferTimeMinutes: 30,
    maxTablesPerCombination: 5,
    maxWastePercentage: 50,
    preferFewerTables: 1,
    allowTablesNearby: false,
    proximityWeight: 0.5,
    maxWastedSeatsForBestFit: 2,
    preferredDirection: 'any',
    clusterProximityThreshold: 50,
    wasteHandlingMode: 'STRICT',
    fallbackMaxWastePercentage: 50,
  })

  useEffect(() => {
    if (show && config) {
      setFormData({
        defaultCleaningTimeMinutes: config.defaultCleaningTimeMinutes,
        autoAllocationEnabled: config.autoAllocationEnabled,
        autoReleaseEnabled: config.autoReleaseEnabled,
        allowOverbooking: config.allowOverbooking,
        bufferTimeMinutes: config.bufferTimeMinutes,
        maxTablesPerCombination: config.maxTablesPerCombination ?? 5,
        maxWastePercentage: config.maxWastePercentage ?? 50,
        preferFewerTables: config.preferFewerTables ?? 1,
        allowTablesNearby: config.allowTablesNearby ?? false,
        proximityWeight: Number(config.proximityWeight ?? 0.5),
        maxWastedSeatsForBestFit: config.maxWastedSeatsForBestFit ?? 2,
        preferredDirection: config.preferredDirection ?? 'any',
        clusterProximityThreshold: Number(config.clusterProximityThreshold ?? 50),
        wasteHandlingMode: config.wasteHandlingMode ?? 'STRICT',
        fallbackMaxWastePercentage: Number(config.fallbackMaxWastePercentage ?? 50),
      })
    }
  }, [show, config])

  useEffect(() => {
    console.log('🎬 [TableConfigModal] Renderizado com props:', {
      show,
      configExists: !!config,
      config,
      environmentName,
    })
  }, [show, config, environmentName])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size='xl' centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            Configurações de Gestão de Mesas
            {environmentName && <small className='text-muted ms-2'>- {environmentName}</small>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey='basic' className='mb-4' variant='pills'>
            {/* TAB 1: GERENCIAMENTO BÁSICO */}
            <Tab eventKey='basic' title={<><i className='bi bi-clock me-2'></i>Gerenciamento Básico</>}>
              <div className='mt-4'>
                <div className='alert alert-info d-flex align-items-center'>
                  <i className='bi bi-info-circle fs-3 me-3'></i>
                  <div>
                    <strong>Configurações por Ambiente</strong>
                    <div className='small'>
                      Estas configurações são específicas para este ambiente e afetam o comportamento
                      automático do sistema de gestão de mesas.
                    </div>
                  </div>
                </div>

                {/* ROW 1: Tempo de Limpeza e Buffer - lado a lado */}
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Tempo de Limpeza Padrão (minutos)</label>
                    <input
                      type='number'
                      className='form-control'
                      value={formData.defaultCleaningTimeMinutes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          defaultCleaningTimeMinutes: Number(e.target.value),
                        })
                      }
                      min='0'
                      step='5'
                    />
                    <div className='form-text'>
                      Tempo necessário para limpar e preparar a mesa após a liberação. Mesas em limpeza não
                      são alocadas automaticamente.
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label required'>Tempo de Buffer (minutos)</label>
                    <input
                      type='number'
                      className='form-control'
                      value={formData.bufferTimeMinutes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bufferTimeMinutes: Number(e.target.value),
                        })
                      }
                      min='0'
                      step='5'
                    />
                    <div className='form-text'>
                      Tempo de segurança entre reservas consecutivas. Garante que haja folga entre
                      atendimentos.
                    </div>
                  </div>
                </div>

                <div className='separator my-7'></div>

                <div className='mb-7'>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='autoAllocationEnabled'
                      checked={formData.autoAllocationEnabled === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          autoAllocationEnabled: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor='autoAllocationEnabled'>
                      <strong>Alocação Automática de Mesas</strong>
                    </label>
                  </div>
                  <div className='form-text ms-10'>
                    Quando ativado, o sistema automaticamente seleciona e aloca as melhores mesas para
                    cada reserva com base na capacidade e disponibilidade. Você ainda pode fazer
                    alocações manuais quando necessário.
                  </div>
                </div>

                <div className='mb-7'>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='autoReleaseEnabled'
                      checked={formData.autoReleaseEnabled === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          autoReleaseEnabled: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor='autoReleaseEnabled'>
                      <strong>Liberação Automática de Mesas</strong>
                    </label>
                  </div>
                  <div className='form-text ms-10'>
                    Quando ativado, as mesas são automaticamente liberadas (mudadas para status de
                    limpeza) após a confirmação de pagamento no WebApp. Desative se preferir liberar
                    manualmente.
                  </div>
                </div>

                <div className='mb-7'>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='allowOverbooking'
                      checked={formData.allowOverbooking === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowOverbooking: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor='allowOverbooking'>
                      <strong>Permitir Superlotação (Overbooking)</strong>
                    </label>
                  </div>
                  <div className='form-text ms-10'>
                    <span className='text-warning'>
                      <i className='bi bi-exclamation-triangle me-1'></i>
                      Use com cautela!
                    </span>{' '}
                    Quando ativado, permite aceitar reservas mesmo quando não há mesas disponíveis. Útil
                    para estabelecimentos que praticam overbooking controlado.
                  </div>
                </div>

                <div className='mb-7'>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='preferFewerTables'
                      checked={formData.preferFewerTables === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          preferFewerTables: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor='preferFewerTables'>
                      <strong>Priorizar Usar Menos Mesas</strong>
                    </label>
                  </div>
                  <div className='form-text ms-10'>
                    <strong>Ativado (Recomendado):</strong> Sistema prefere usar menos mesas, mesmo que tenha um pouco mais de desperdício.
                    Facilita operação e movimentação.
                    <div className='mt-2'>
                      <strong>Desativado:</strong> Sistema tenta todas as combinações para minimizar lugares vazios.
                      Melhor aproveitamento do espaço, mas pode usar mais mesas que o necessário.
                    </div>
                    <div className='mt-2'>
                      <strong>Exemplo para 12 pessoas:</strong>
                      <ul className='mb-0 mt-1'>
                        <li><strong>Com prioridade:</strong> 2 mesas de 8 (16 lugares, 4 vazios) ✓</li>
                        <li><strong>Sem prioridade:</strong> 3 mesas de 4 (12 lugares, 0 vazios)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            {/* TAB 2: PROXIMIDADE DE MESAS */}
            <Tab eventKey='proximity' title={<><i className='bi bi-pin-map me-2'></i>Proximidade</>}>
              <div className='mt-4'>
                <div className='mb-7'>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='allowTablesNearby'
                      checked={formData.allowTablesNearby === true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowTablesNearby: e.target.checked,
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor='allowTablesNearby'>
                      <strong>Permitir Mesas Próximas</strong>
                    </label>
                  </div>
                  <div className='form-text ms-10'>
                    Quando ativado, o sistema considera mesas fisicamente próximas na visualização para
                    possível unificação de reservas, otimizando o uso do espaço e agrupando clientes
                    relacionados.
                  </div>
                </div>

                {formData.allowTablesNearby && (
                  <div className='mb-7'>
                    <label className='form-label required'>
                      <i className='bi bi-percent me-2 text-primary'></i>
                      Peso de Proximidade
                    </label>
                    <div className='d-flex align-items-center gap-3'>
                      <input
                        type='number'
                        className='form-control'
                        value={formData.proximityWeight ?? 0.5}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            proximityWeight: Number(e.target.value),
                          })
                        }
                        min='0.1'
                        max='2.0'
                        step='0.1'
                      />
                      <div className='d-flex align-items-center'>
                        <span className='badge bg-light-primary text-dark ms-2'>
                          {(formData.proximityWeight ?? 0.5).toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <div className='form-text mt-3'>
                      Controla o peso da distância entre mesas no cálculo de alocação. Valores maiores 
                      priorizam mesas mais próximas.
                      <div className='mt-2'>
                        <strong>Valores comuns:</strong>
                        <ul className='mb-0 mt-1 small'>
                          <li><strong>0.1-0.3:</strong> Baixa prioridade à proximidade (pouco impacto)</li>
                          <li><strong>0.5:</strong> Padrão (balanceado) ⭐</li>
                          <li><strong>1.0:</strong> Alta prioridade à proximidade</li>
                          <li><strong>1.5-2.0:</strong> Muito alta prioridade à proximidade</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Tab>

            {/* TAB 3: UNIFICAÇÃO DE MESAS */}
            <Tab eventKey='unification' title={<><i className='bi bi-grid-3x3-gap me-2'></i>Unificação</>}>
              <div className='mt-4'>
                <div className='alert alert-light-primary d-flex align-items-start'>
                  <i className='bi bi-lightbulb fs-3 me-3 text-primary'></i>
                  <div className='small'>
                    <strong>Como funciona:</strong> Quando um grupo precisa de mais lugares do que uma mesa oferece, 
                    o sistema pode unir várias mesas. Estas configurações controlam como essa unificação é feita.
                  </div>
                </div>

                {/* ROW 2: Máximo de Mesas e Desperdício Máximo - lado a lado */}
                <div className='row g-3 mt-0'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Máximo de Mesas por Combinação</label>
                    <input
                      type='number'
                      className='form-control'
                      value={formData.maxTablesPerCombination}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maxTablesPerCombination: Number(e.target.value),
                        })
                      }
                      min='1'
                      max='10'
                      step='1'
                    />
                    <div className='form-text'>
                      Número máximo de mesas que podem ser unidas para um único grupo (1-10).
                      <div className='mt-1'>
                        <strong>Exemplos:</strong>
                        <ul className='mb-0 mt-1 small'>
                          <li><strong>3-4 mesas:</strong> Restaurante pequeno/médio</li>
                          <li><strong>5-7 mesas:</strong> Restaurante grande</li>
                          <li><strong>8-10 mesas:</strong> Eventos e banquetes</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label required'>Desperdício Máximo de Capacidade (%)</label>
                    <input
                      type='number'
                      className='form-control'
                      value={formData.maxWastePercentage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maxWastePercentage: Number(e.target.value),
                        })
                      }
                      min='0'
                      max='100'
                      step='5'
                    />
                    <div className='form-text'>
                      Percentual máximo de lugares vazios aceitável em uma combinação (0-100%).
                      <div className='mt-1'>
                        <strong>Recomendações:</strong>
                        <ul className='mb-0 mt-1 small'>
                          <li><strong>20-30%:</strong> Fine dining</li>
                          <li><strong>40-50%:</strong> Restaurante padrão</li>
                          <li><strong>60-100%:</strong> Eventos</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ROW 3: Best Fit e Preferência de Geometria - lado a lado */}
                <div className='row g-3 mt-0'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Máximo de Lugares Vazios na Fase Best Fit</label>
                    <input
                      type='number'
                      className='form-control'
                      value={formData.maxWastedSeatsForBestFit ?? 2}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maxWastedSeatsForBestFit: Number(e.target.value),
                        })
                      }
                      min='0'
                      max='5'
                      step='1'
                    />
                    <div className='form-text'>
                      Número máximo absoluto de assentos vazios na fase "Best Fit" (0-5).
                      <div className='mt-1 small'>
                        <strong>Valores:</strong>
                        <ul className='mb-0 mt-1'>
                          <li><strong>0-1:</strong> Restritivo</li>
                          <li><strong>2:</strong> Padrão/recomendado ⭐</li>
                          <li><strong>3-5:</strong> Tolerante</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label required'>
                      <i className='bi bi-diagram-3 me-2 text-primary'></i>
                      Preferência de Posição de Mesas (Geometria)
                    </label>
                    <select
                      className='form-select'
                      value={formData.preferredDirection ?? 'any'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          preferredDirection: e.target.value,
                        })
                      }
                    >
                      <option value='any'>Qualquer Geometria (Padrão)</option>
                      <option value='h_v'>Apenas Alinhadas (Horizontal/Vertical)</option>
                      <option value='cluster'>Apenas Próximas (Cluster)</option>
                    </select>
                    <div className='form-text mt-2'>
                      Controla como o sistema prefere combinar mesas geograficamente:
                      <ul className='mb-0 mt-2'>
                        <li>
                          <strong>"Qualquer Geometria" (any):</strong> Aceita todas as combinações, sem restrição de posição
                          <div className='small text-muted mt-1'>
                            Ideal para restaurantes com espaço flexível. Maximiza opções de combinação.
                          </div>
                        </li>
                        <li className='mt-2'>
                          <strong>"Apenas Alinhadas" (h_v):</strong> Mesas devem estar alinhadas horizontalmente ou verticalmente
                          <div className='small text-muted mt-1'>
                            Rejeita combinações diagonais. Mantém linhas visuais limpas. Ideal para layouts estruturados.
                          </div>
                        </li>
                        <li className='mt-2'>
                          <strong>"Apenas Próximas" (cluster):</strong> Mesas devem estar muito próximas (distância &lt; 200px ≈ 53cm)
                          <div className='small text-muted mt-1'>
                            Garante combos compactas e agrupadas. Ideal para restaurantes com movimento constante.
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {formData.preferredDirection === 'cluster' && (
                  <div className='row g-3 mt-0'>
                    <div className='col-12'>
                      <label className='form-label required'>
                        <i className='bi bi-arrow-left-right me-2 text-info'></i>
                        Distância Máxima de Proximidade (Cluster)
                      </label>
                      <div className='d-flex align-items-end gap-3'>
                        <div className='flex-grow-1'>
                          <input
                            type='range'
                            className='form-range'
                            min='20'
                            max='500'
                            step='10'
                            value={formData.clusterProximityThreshold ?? 50}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                clusterProximityThreshold: Number(e.target.value),
                              })
                            }
                          />
                          <div className='small text-muted mt-2'>
                            <strong>Valor atual:</strong> {formData.clusterProximityThreshold ?? 50}cm (~{Math.round(((formData.clusterProximityThreshold ?? 50) * 96) / 2.54)}px)
                          </div>
                        </div>
                        <div className='text-center' style={{minWidth: '100px'}}>
                          <div className='fs-5 fw-bold text-primary'>
                            {formData.clusterProximityThreshold ?? 50}cm
                          </div>
                          <div className='small text-muted'>
                            (~{Math.round(((formData.clusterProximityThreshold ?? 50) * 96) / 2.54)}px)
                          </div>
                        </div>
                      </div>
                      <div className='form-text mt-3'>
                        <strong>Explicação:</strong> Mesas serão combinadas apenas se a distância total entre elas for menor que este valor em centímetros.
                        <ul className='mt-2 mb-0 small'>
                          <li><strong>20-50cm:</strong> Combos muito compactas</li>
                          <li><strong>50-150cm:</strong> Recomendado (maioria)</li>
                          <li><strong>200-500cm:</strong> Combos soltas</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Tab>

            {/* TAB 4: ESTRATÉGIA DE DESPERDÍCIO */}
            <Tab eventKey='waste' title={<><i className='bi bi-pie-chart me-2'></i>Desperdício</>}>
              <div className='mt-4'>
                <div className='alert alert-light-success d-flex align-items-start'>
                  <i className='bi bi-lightbulb fs-3 me-3 text-success'></i>
                  <div className='small'>
                    <strong>Como funciona:</strong> Define como o sistema tolera a capacidade não utilizada 
                    quando aloca clientes a mesas. Modos mais flexíveis aceitam maior desperdício para acomodar grupos.
                  </div>
                </div>

                <div className='mb-7'>
                  <label className='form-label required'>
                    <i className='bi bi-shuffle me-2 text-success'></i>
                    Modo de Desperdício
                  </label>
                  <div>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='radio'
                        id='wasteModeStrict'
                        name='wasteHandlingMode'
                        checked={formData.wasteHandlingMode === 'STRICT'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            wasteHandlingMode: 'STRICT',
                          })
                        }
                      />
                      <label className='form-check-label' htmlFor='wasteModeStrict'>
                        <strong>STRICT</strong> - Desperdício Máximo Uniforme (padrão)
                      </label>
                    </div>
                    <div className='form-text ms-26 mb-3'>
                      Aplica o limite de desperdício <strong>máximo de {formData.maxWastePercentage}%</strong> a todas as fases de alocação.
                      <div className='mt-2 small'>
                        ✅ <strong>Melhor para:</strong> Fine dining, restaurantes que priorizam otimizar espaço
                        <br />
                        ❌ <strong>Problema:</strong> Pode rejeitar grupos que carecem de apenas alguns lugares, 
                        como Paulo Brito (5 pessoas, 37.5% de waste em 2 mesas de 4).
                      </div>
                    </div>

                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='radio'
                        id='wasteModeFlexible'
                        name='wasteHandlingMode'
                        checked={formData.wasteHandlingMode === 'FLEXIBLE'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            wasteHandlingMode: 'FLEXIBLE',
                          })
                        }
                      />
                      <label className='form-check-label' htmlFor='wasteModeFlexible'>
                        <strong>FLEXIBLE</strong> - Aumenta Tolerância Proporcionalmente
                      </label>
                    </div>
                    <div className='form-text ms-26 mb-3'>
                      Aumenta o limite de desperdício em <strong>~90%</strong> da tolerância configurada 
                      (multiplicando <strong>{formData.maxWastePercentage}% × 1.9</strong>).
                      <div className='mt-2 small'>
                        ✅ <strong>Melhor para:</strong> Restaurantes intermediários, maior flexibilidade
                        <br />
                        ✅ <strong>Comportamento:</strong> Proporcional à configuração. Ex: se maxWaste=20%, 
                        usa ~38%; se maxWaste=30%, usa ~57%; se maxWaste=50%, usa ~95%
                        <br />
                        ✅ <strong>Exemplo prático:</strong> Paulo Brito (5 pessoas, 37.5% waste) é aceito 
                        quando maxWaste=20% (threshold ~38%)
                      </div>
                    </div>

                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='radio'
                        id='wasterModeFallback'
                        name='wasteHandlingMode'
                        checked={formData.wasteHandlingMode === 'FALLBACK'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            wasteHandlingMode: 'FALLBACK',
                          })
                        }
                      />
                      <label className='form-check-label' htmlFor='wasterModeFallback'>
                        <strong>FALLBACK</strong> - Progressivo (RECOMENDADO)
                      </label>
                    </div>
                    <div className='form-text ms-26'>
                      Começa <strong>STRICT ({formData.maxWastePercentage}%)</strong> nas fases iniciais e, 
                      se nenhuma combinação for encontrada, relaxa para{' '}
                      <strong>{formData.fallbackMaxWastePercentage || 50}%</strong> na fase GREEDY final.
                      <div className='mt-2 small'>
                        ✅ <strong>Melhor para:</strong> Restaurantes que querem otimizar espaço mas aceitar reservas 
                        que seriam recusadas
                        <br />
                        ✅ <strong>Soluciona:</strong> Paulo Brito (5 pessoas): STRICT rejeita em fase 0/1, 
                        mas FALLBACK a aceita em GREEDY com 37.5% &lt; 50%
                        <br />
                        ✅ <strong>Comportamento:</strong> Progressivo e justo - preserva otimização sem prejudicar receita
                      </div>
                    </div>
                  </div>
                </div>

                {formData.wasteHandlingMode === 'FALLBACK' && (
                  <div className='mb-7'>
                    <label className='form-label required'>
                      <i className='bi bi-percent me-2 text-success'></i>
                      Limite de Desperdício para FALLBACK (Fase GREEDY)
                    </label>
                    <div className='d-flex align-items-end gap-3'>
                      <div className='flex-grow-1'>
                        <input
                          type='range'
                          className='form-range'
                          min='25'
                          max='100'
                          step='5'
                          value={formData.fallbackMaxWastePercentage || 50}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              fallbackMaxWastePercentage: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className='text-center' style={{minWidth: '80px'}}>
                        <div className='fs-5 fw-bold text-success'>
                          {formData.fallbackMaxWastePercentage || 50}%
                        </div>
                      </div>
                    </div>
                    <div className='form-text mt-3'>
                      <strong>Explicação:</strong> Quando o modo FALLBACK relaxa restrições, este é o limite 
                      máximo de desperdício permitido na fase "GREEDY" (última fase). Aplicável <strong>APENAS</strong> 
                      quando modo = FALLBACK (fases 0 e 1 usam sempre {formData.maxWastePercentage}%).
                      <ul className='mt-2 mb-0'>
                        <li><strong>25-35%:</strong> Pouco relaxo (quase como padrão)</li>
                        <li><strong>40-50%:</strong> Balanceado (recomendado) - Aceita casos como Paulo</li>
                        <li><strong>60-100%:</strong> Muito relaxo (aceita quase tudo)</li>
                      </ul>
                    </div>
                    <div className='mt-3 p-3 bg-light-success rounded'>
                      <strong className='text-success'>📊 Exemplo Prático (Paulo Brito):</strong>
                      <div className='small mt-2'>
                        <strong>Cenário:</strong> 5 clientes, 2 mesas de 4 lugares = 8 lugares, waste = 37.5%
                        <ul className='mb-0 mt-2'>
                          <li>
                            <strong>STRICT/FLEXIBLE:</strong> ❌ Rejeitado porque 37.5% &gt; 20% (STRICT) 
                            ou mesmo com threshold mais alto
                          </li>
                          <li>
                            <strong>FALLBACK + 50%:</strong> ✅ Aceito porque 37.5% &lt; 50% 
                            na fase GREEDY (última tentativa)
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancelar
          </button>
          <button type='submit' className='btn btn-primary'>
            Salvar Configurações
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
